// @ts-nocheck -- Account-pinned rune state and browser capability APIs.
import { auth } from '#lib/stores/auth.svelte.js';
import { makePlaceAlertsClient, PlaceAlertError } from '#lib/PlaceAlertsClient.js';
import { setPushOwner } from '#lib/placeAlertPush.js';

const INSTALL_KEY = 'mylocation.place-alert-installation';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Dependency seams exercise late responses and account changes without real accounts. */
export function createPlaceAlerts({ authState = auth, clientFactory = makePlaceAlertsClient, writeOwner = setPushOwner } = {}) {
	let data = $state(/** @type {{alerts:any[],aboutMe:any[],capabilities:{webPush:boolean,webPushPublicKey:string|null,fcm:boolean}}} */ ({ alerts: [], aboutMe: [], capabilities: { webPush: false, webPushPublicKey: null, fcm: false } }));
	let ready = $state(false);
	let loading = $state(false);
	let error = $state('');
	let busy = $state('');
	let editor = $state(/** @type {{key:string,friendUid?:string,place?:any}|null} */ (null));
	let delivery = $state('prompt');
	let session = null;
	let accountKnown = false;
	let generation = 0;
	let readRevision = 0;
	let controller = null;
	let inFlight = null;
	let lastToken = null;
	let watchers = 0;
	let timer = null;
	let deviceQueue = Promise.resolve();
	let ownerQueue = Promise.resolve();
	let registeredUid = '';
	let devicePending = false;
	let lastDraft = '';
	let requestId = '';
	const operations = new Set();
	const current = (s, g) => s && generation === g && session === s && authState.isSessionCurrent(s);
	const owner = (uid) => { ownerQueue = ownerQueue.catch(() => {}).then(() => writeOwner(uid)); return ownerQueue; };
	function reset() { data = { alerts: [], aboutMe: [], capabilities: { webPush: false, webPushPublicKey: null, fcm: false } }; ready = false; error = ''; busy = ''; editor = null; loading = false; }
	function installationId(create = false) {
		try {
			const stored = localStorage.getItem(INSTALL_KEY);
			if (stored && UUID.test(stored)) return stored;
			if (!create) return null;
			const id = crypto.randomUUID();
			localStorage.setItem(INSTALL_KEY, id);
			return id;
		} catch { return null; }
	}
	function client(s, signal, cleanupToken = null) {
		return clientFactory(async (force) => {
			if (cleanupToken) return cleanupToken;
			if (!authState.isSessionCurrent(s)) throw new PlaceAlertError('account_changed');
			const token = await s.user.getIdToken(force);
			if (!authState.isSessionCurrent(s)) throw new PlaceAlertError('account_changed');
			if (session === s) lastToken = token;
			return token;
		}, { signal });
	}
	function syncAccount() {
		const next = authState.captureSession();
		if (accountKnown && (next?.uid || '') === (session?.uid || '') && (next?.epoch ?? -1) === (session?.epoch ?? -1)) return;
		accountKnown = true;
		generation++;
		readRevision++;
		controller?.abort(); controller = null; inFlight = null;
		const old = session, token = lastToken;
		session = next; lastToken = null; registeredUid = '';
		reset();
		void owner(next?.uid || '').catch(() => { delivery = 'error'; });
		if (old) queueDeviceCleanup(old, token);
		// Re-register an already permitted subscription on every account change,
		// including sign-in from the map rather than the Friends page.
		if (next) queueMicrotask(() => { if (session?.uid === next.uid && authState.isSessionCurrent(next)) void refresh(); });
	}
	function queueDeviceCleanup(s, token) {
		const id = installationId();
		deviceQueue = deviceQueue.catch(() => {}).then(async () => {
			if (id && token) await client(s, undefined, token).unregister(id).catch(() => {});
			try {
				const registration = await navigator.serviceWorker?.getRegistration();
				const shown = await registration?.getNotifications();
				for (const notification of shown || []) if (notification.data?.recipientUid === s.uid) notification.close();
			} catch { /* notification cleanup is best effort */ }
		});
		return deviceQueue;
	}
	async function teardown(uid) {
		if (session?.uid !== uid) return;
		const previous = session, token = lastToken;
		generation++;
		readRevision++;
		controller?.abort(); controller = null; inFlight = null;
		session = null; lastToken = null; registeredUid = ''; reset();
		await owner('').catch(() => {});
		await Promise.allSettled([...operations]);
		await queueDeviceCleanup(previous, token);
	}
	const unregister = authState.registerSessionTeardown(teardown);
	function refresh() {
		syncAccount();
		if (!session) return Promise.resolve();
		if (inFlight) return inFlight;
		const s = session, g = generation;
		const revision = ++readRevision;
		controller = new AbortController();
		const signal = controller.signal;
		loading = true;
		const promise = (async () => {
			try {
				const result = await client(s, signal).list();
				if (!current(s, g) || revision !== readRevision) return;
				data = { alerts: Array.isArray(result?.alerts) ? result.alerts : [], aboutMe: Array.isArray(result?.aboutMe) ? result.aboutMe : [], capabilities: result?.capabilities || { webPush: false, webPushPublicKey: null, fcm: false } };
				ready = true; error = '';
				updateDelivery();
				if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && registeredUid !== s.uid) void enableNotifications(false);
			} catch (cause) {
				if (current(s, g) && revision === readRevision) { error = cause?.status === 404 || cause?.status === 503 ? 'unavailable' : 'load'; ready = true; }
			} finally { if (current(s, g) && revision === readRevision) loading = false; if (inFlight === promise) inFlight = null; }
		})();
		inFlight = promise;
		return promise;
	}
	function watch() {
		watchers++;
		void refresh();
		if (!timer) timer = setInterval(() => { if (!document.hidden) void refresh(); }, 20_000);
		return () => { watchers = Math.max(0, watchers - 1); if (!watchers) { clearInterval(timer); timer = null; } };
	}
	async function mutate(key, action) {
		syncAccount();
		if (!session) throw new PlaceAlertError('not_signed_in');
		if (busy) throw new PlaceAlertError('busy');
		const s = session, g = generation;
		busy = key;
		const operation = Promise.resolve().then(() => action(client(s)));
		operations.add(operation);
		try {
			const result = await operation;
			if (!current(s, g)) throw new PlaceAlertError('account_changed');
			// Cancel an older list request before pulling the mutation result.
			controller?.abort(); inFlight = null;
			await refresh();
			if (!current(s, g)) throw new PlaceAlertError('account_changed');
			return result;
		} finally { operations.delete(operation); if (current(s, g)) busy = ''; }
	}
	function openEditor(options = {}) {
		syncAccount();
		if (!session) { authState.openSignIn(); return; }
		lastDraft = ''; requestId = '';
		editor = { ...options, key: crypto.randomUUID() };
	}
	function create(draft) {
		const encoded = JSON.stringify(draft);
		if (!requestId || lastDraft !== encoded) { requestId = crypto.randomUUID(); lastDraft = encoded; }
		return mutate('create', api => api.create({ ...draft, id: requestId }));
	}
	function updateDelivery() {
		if (typeof window === 'undefined') return;
		if (!data.capabilities.webPush || !data.capabilities.webPushPublicKey) { delivery = 'unavailable'; return; }
		if (!('Notification' in window) || !('PushManager' in window) || !('serviceWorker' in navigator)) {
			delivery = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'install' : 'unsupported'; return;
		}
		delivery = Notification.permission === 'denied' ? 'denied' : registeredUid === session?.uid ? 'enabled' : 'prompt';
	}
	async function enableNotifications(requestPermission = true) {
		syncAccount(); updateDelivery();
		if (devicePending) return deviceQueue;
		const s = session, g = generation;
		if (!s || ['unavailable', 'unsupported', 'install', 'denied'].includes(delivery)) return;
		// Request directly from the button gesture, before any asynchronous work.
		const permission = Notification.permission === 'default' && requestPermission ? Notification.requestPermission() : Promise.resolve(Notification.permission);
		const key = data.capabilities.webPushPublicKey;
		devicePending = true;
		deviceQueue = deviceQueue.catch(() => {}).then(async () => {
			try {
				if (await permission !== 'granted') { if (current(s, g)) updateDelivery(); return; }
				if (!current(s, g)) return;
				delivery = 'working';
				const id = installationId(true);
				if (!id) throw new Error('storage');
				const registration = await navigator.serviceWorker.getRegistration();
				if (!registration) throw new Error('service_worker_not_ready');
				if (!current(s, g)) return;
				const bytes = Uint8Array.from(atob(key.replace(/-/g, '+').replace(/_/g, '/')), char => char.charCodeAt(0));
				let subscription = await registration.pushManager.getSubscription();
				if (subscription?.options.applicationServerKey && !equalBytes(new Uint8Array(subscription.options.applicationServerKey), bytes)) {
					await subscription.unsubscribe(); subscription = null;
				}
				if (!current(s, g)) return;
				subscription ||= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
				if (!current(s, g)) return;
				await client(s).register(id, subscription.toJSON(), document.documentElement.lang || 'en');
				if (current(s, g)) { registeredUid = s.uid; delivery = 'enabled'; }
			} catch { if (current(s, g)) delivery = 'error'; }
			finally { devicePending = false; }
		});
		return deviceQueue;
	}
	return {
		get alerts() { return authState.isSessionCurrent(session) ? data.alerts : []; },
		get aboutMe() { return authState.isSessionCurrent(session) ? data.aboutMe : []; },
		get ready() { return ready; }, get loading() { return loading; }, get error() { return error; }, get busy() { return busy; },
		get editor() { return editor; }, get delivery() { return delivery; },
		syncAccount, refresh, watch, openEditor, closeEditor() { if (!busy) editor = null; }, create,
		action: (rule, action) => mutate(rule.id, api => api.action(rule, action)),
		remove: rule => mutate(rule.id, api => api.remove(rule)),
		enableNotifications,
		async destroy() { clearInterval(timer); await teardown(session?.uid); unregister(); }
	};
}
function equalBytes(a, b) { return a.length === b.length && a.every((value, i) => value === b[i]); }
export const placeAlerts = createPlaceAlerts();
